import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import {
  ClipboardList,
  Search,
  RefreshCw,
  Upload,
  FileSpreadsheet,
  Plus,
  Pencil,
  Trash2,
  Filter,
  X,
} from "lucide-react";
import { toast } from "sonner";
import api, { adminReq } from "../../lib/api";
import {
  AID_PROGRAMS,
  AID_ACTIONS,
  AID_COE_STATUS,
  AID_YES_NO,
  PROVINCES,
} from "../../lib/data";

// -----------------------------------------------------------------------------
// Constants & helpers
// -----------------------------------------------------------------------------
const BRAND = {
  green: "#0B5E2A",
  greenDark: "#0a4d24",
  gold: "#F0B93C",
  surface: "#fbfdf9",
  hover: "#f4f9f0",
  subText: "#6b7e6d",
};

const ACTION_STYLES = {
  Pending:       { bg: "#FEF4DB", fg: "#8a5e00" },
  "ISSUE LOA":   { bg: "#FEF1E1", fg: "#a05a1a" },
  "ISSUE CLOA":  { bg: "#FEF1E1", fg: "#a05a1a" },
  "LOA ISSUED":  { bg: "#E1F0F3", fg: "#0f5a6e" },
  "CLOA ISSUED": { bg: "#E1F0F3", fg: "#0f5a6e" },
  "APPENDIX 3":  { bg: "#EDE9F7", fg: "#4b3a94" },
  "COE SENT":    { bg: "#E1F3E4", fg: BRAND.green },
};

const emptyRecord = {
  student_id: "",
  last_name: "",
  first_name: "",
  program: "",
  province: "",
  agent: "",
  start_date: "",
  end_date: "",
  email: "",
  passport_pr_card: "",
  photo_id: "",
  transcript_12_acsw: "",
  language_proof: "",
  action: "",
  pending_documents: "",
  appendix_3: "",
  coe_status: "not_done",
  sec_form: "",
  comments: "",
};

// Legacy tolerance: coerce booleans/strings on doc checklist to "" | "yes" | "no"
const coerceYesNo = (v) => {
  if (v === true) return "yes";
  if (v === false) return "no";
  if (v === null || v === undefined) return "";
  const s = String(v).trim().toLowerCase();
  if (["", "-", "—", "n/a", "na"].includes(s)) return "";
  if (["y", "yes", "true", "1", "✓", "check", "checked"].includes(s)) return "yes";
  if (["n", "no", "false", "0", "unchecked"].includes(s)) return "no";
  return "";
};

// -----------------------------------------------------------------------------
// Pills / badges
// -----------------------------------------------------------------------------
const ProgramPill = ({ code }) =>
  !code ? (
    <span className="text-slate-300">—</span>
  ) : (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border"
      style={{
        background: "#E4F3E5",
        color: BRAND.green,
        borderColor: "#c6e3c9",
      }}
    >
      {code}
    </span>
  );

const ProvincePill = ({ p }) =>
  !p ? (
    <span className="text-slate-300">—</span>
  ) : (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-slate-100 text-slate-700 border-slate-200">
      {p}
    </span>
  );

const DocPill = ({ v }) => {
  const n = coerceYesNo(v);
  if (n === "yes")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        Yes
      </span>
    );
  if (n === "no")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
        No
      </span>
    );
  return <span className="text-slate-300">—</span>;
};

const ActionPill = ({ v }) => {
  if (!v) return <span className="text-slate-300">—</span>;
  const s = ACTION_STYLES[v] || { bg: "#eef2f7", fg: "#334155" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
      style={{ background: s.bg, color: s.fg }}
    >
      {v}
    </span>
  );
};

const CoeBadge = ({ v }) => {
  const done = String(v || "").toLowerCase() === "done";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${
        done
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-amber-50 text-amber-700 border-amber-200"
      }`}
    >
      {done ? "Done" : "Pending"}
    </span>
  );
};

const SecBadge = ({ v }) => {
  const n = coerceYesNo(v);
  if (n === "yes")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
        Yes
      </span>
    );
  if (n === "no")
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
        No
      </span>
    );
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500 border border-slate-200">
      —
    </span>
  );
};

// -----------------------------------------------------------------------------
// Excel-style column filter
// -----------------------------------------------------------------------------
function FilterableHeader({
  col,
  label,
  values,
  selected,
  onChange,
  valueLabel = (v) => (v === "" ? "(Blanks)" : v),
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const esc = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", handler);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const uniq = useMemo(() => {
    const set = new Set(values.map((v) => (v === undefined || v === null ? "" : v)));
    return Array.from(set).sort((a, b) => {
      if (a === "") return 1;
      if (b === "") return -1;
      return String(a).localeCompare(String(b));
    });
  }, [values]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return uniq;
    return uniq.filter((v) => valueLabel(v).toLowerCase().includes(needle));
  }, [uniq, q, valueLabel]);

  const active = selected && selected.size > 0;
  const allChecked = filtered.length > 0 && filtered.every((v) => !selected || selected.has(v));

  const toggle = (v) => {
    const next = new Set(selected || []);
    next.has(v) ? next.delete(v) : next.add(v);
    if (next.size === 0) onChange(null);
    else onChange(next);
  };
  const clearCol = () => onChange(null);
  const selectAllVisible = () => {
    const next = new Set(selected || []);
    filtered.forEach((v) => next.add(v));
    onChange(next.size ? next : null);
  };

  return (
    <div className="inline-flex items-center gap-1 relative" ref={ref}>
      <span>{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`p-0.5 rounded transition-colors ${
          active
            ? "text-white bg-white/25"
            : "text-white/60 hover:text-white hover:bg-white/10"
        }`}
        data-testid={`aid-filter-${col}`}
        title={`Filter ${label}`}
      >
        <Filter className="w-3 h-3" />
      </button>
      {open && (
        <div
          className="absolute top-full left-0 mt-1 z-30 w-64 rounded-lg shadow-xl border border-slate-200 bg-white text-slate-700 normal-case tracking-normal text-xs"
          data-testid={`aid-filter-menu-${col}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="font-semibold text-slate-800">{label}</div>
            <div className="text-[10px] text-slate-400">Filter · {col}</div>
          </div>
          <div className="p-2">
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search values…"
              className="w-full rounded border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-emerald-500"
              data-testid={`aid-filter-search-${col}`}
            />
            {q.trim() && (
              <label className="mt-2 flex items-center gap-2 text-[11px] text-slate-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={() =>
                    allChecked ? clearCol() : selectAllVisible()
                  }
                />
                Select all matches
              </label>
            )}
          </div>
          <div className="max-h-56 overflow-y-auto border-t border-slate-100">
            {filtered.length === 0 ? (
              <div className="text-center text-slate-400 py-4 text-[11px]">
                No matches
              </div>
            ) : (
              filtered.map((v) => {
                const checked = selected ? selected.has(v) : false;
                return (
                  <label
                    key={String(v)}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(v)}
                    />
                    <span
                      className={v === "" ? "text-slate-400 italic" : ""}
                    >
                      {valueLabel(v)}
                    </span>
                  </label>
                );
              })
            )}
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-slate-100 bg-slate-50/70">
            <button
              type="button"
              className="text-[11px] text-rose-600 hover:underline"
              onClick={clearCol}
              data-testid={`aid-filter-clear-${col}`}
            >
              Clear
            </button>
            <button
              type="button"
              className="text-[11px] text-slate-600 hover:underline"
              onClick={() => onChange(new Set(uniq))}
            >
              Select all
            </button>
            <button
              type="button"
              className="text-[11px] font-semibold text-white px-2.5 py-1 rounded"
              style={{ background: BRAND.green }}
              onClick={() => setOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------
export default function AdminStudentAid() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({}); // { col: Set(values) }
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [recruiters, setRecruiters] = useState([]);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/student-aid", adminReq());
      setRows(Array.isArray(data) ? data : []);
    } catch {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadRecruiters = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/recruiters", adminReq());
      setRecruiters(Array.isArray(data) ? data : []);
    } catch {
      // non-fatal
    }
  }, []);

  useEffect(() => {
    load();
    loadRecruiters();
  }, [load, loadRecruiters]);

  // Search + filter
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (needle) {
        const hay = [
          r.student_id, r.first_name, r.last_name, r.email, r.program, r.province,
          r.agent, r.action, r.comments, r.pending_documents, r.appendix_3,
        ]
          .filter(Boolean)
          .map((s) => String(s).toLowerCase())
          .join(" | ");
        if (!hay.includes(needle)) return false;
      }
      for (const [col, set] of Object.entries(filters)) {
        if (!set || set.size === 0) continue;
        const v = r[col] ?? "";
        if (!set.has(v)) return false;
      }
      return true;
    });
  }, [rows, q, filters]);

  const setColFilter = (col) => (set) =>
    setFilters((prev) => {
      const next = { ...prev };
      if (!set || set.size === 0) delete next[col];
      else next[col] = set;
      return next;
    });

  const clearAllFilters = () => setFilters({});

  const activeFilters = Object.entries(filters).filter(
    ([, set]) => set && set.size > 0,
  );

  // Save (create or update)
  const save = async (record) => {
    try {
      if (record.id) {
        await api.patch(
          `/admin/student-aid/${record.id}`,
          record,
          adminReq(),
        );
        toast.success("Record updated");
      } else {
        await api.post("/admin/student-aid", record, adminReq());
        toast.success("Record created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const removeOne = async (id) => {
    if (!window.confirm("Delete this record? It can be restored from Trash.")) return;
    try {
      await api.delete(`/admin/student-aid/${id}`, adminReq());
      toast.success("Record deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  // Excel import / export
  const openImport = () => fileInputRef.current?.click();
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/\.xlsx?$/i.test(file.name)) {
      toast.error("Please upload an .xlsx file");
      return;
    }
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post(
        "/admin/student-aid/import",
        form,
        {
          ...adminReq(),
          headers: {
            ...(adminReq().headers || {}),
            "Content-Type": "multipart/form-data",
          },
        },
      );
      const created = data.created || 0;
      const updated = data.updated || 0;
      const skipped = data.skipped || 0;
      const errs = (data.errors || []).length;
      toast.success(
        `${created} created · ${updated} updated · ${skipped} skipped · ${errs} error${errs !== 1 ? "s" : ""}`,
      );
      if (errs > 0) {
        console.warn("Student Aid import errors:", data.errors);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const downloadExport = async () => {
    try {
      const res = await api.get("/admin/student-aid/export", {
        ...adminReq(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      a.href = url;
      a.download = `HCC-Student-Aid-${today}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download");
    }
  };

  // Agent options — recruiters (active or otherwise) + preserve legacy string
  const agentOptions = useMemo(() => {
    const seenLabels = new Set();
    const opts = recruiters
      .map((r) => {
        const status = r.status || "";
        let tail = "";
        if (status === "active") tail = "Signed";
        else if (status === "agreement_signed") tail = "Signed";
        else if (status === "agreement_sent") tail = "Pending";
        else if (status === "pending") tail = "Pending";
        else if (status === "rejected") tail = "Rejected";
        else tail = "Internal";
        const label = `${r.full_name || r.email || "Recruiter"} — ${r.company_name || "—"} • ${tail}`;
        seenLabels.add(label);
        return { value: label, label };
      });
    // Preserve any legacy free-text agent values not present in recruiters
    const legacy = new Set(
      rows.map((r) => r.agent).filter((v) => v && !seenLabels.has(v)),
    );
    return [...opts, ...Array.from(legacy).map((v) => ({ value: v, label: `${v} (legacy)` }))];
  }, [recruiters, rows]);

  return (
    <div
      className="space-y-4"
      data-testid="student-aid-tab"
      style={{ background: BRAND.surface }}
    >
      {/* Toolbar */}
      <div
        className="rounded-2xl p-4 flex flex-col md:flex-row md:items-center gap-3 shadow-sm border"
        style={{ background: "white", borderColor: "#dfe7d8" }}
      >
        <div className="flex items-center gap-2 text-slate-700">
          <ClipboardList className="w-5 h-5" style={{ color: BRAND.green }} />
          <span className="font-semibold" data-testid="aid-count">
            {filtered.length} record{filtered.length !== 1 ? "s" : ""}
          </span>
          <span className="text-slate-400 text-xs">
            {rows.length !== filtered.length && `(of ${rows.length})`}
          </span>
        </div>
        <div className="flex-1 flex items-center gap-2 bg-slate-100 rounded-lg px-3 py-2 max-w-xl md:ml-auto">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search across ID, name, program, agent, action, comments..."
            className="bg-transparent outline-none text-sm flex-1"
            data-testid="aid-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xlsm"
            onChange={onFileChosen}
            className="hidden"
            data-testid="aid-import-file"
          />
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-700 text-sm hover:bg-slate-50"
            data-testid="aid-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={openImport}
            disabled={importing}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-60"
            data-testid="aid-import-btn"
          >
            <Upload className="w-4 h-4" />
            {importing ? "Uploading..." : "Upload Excel"}
          </button>
          <button
            onClick={downloadExport}
            disabled={rows.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 bg-white text-slate-700 text-sm hover:bg-slate-50 disabled:opacity-40"
            data-testid="aid-export-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Download Excel
          </button>
          <button
            onClick={() => setEditing({ ...emptyRecord })}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-white text-sm font-semibold shadow-sm"
            style={{ background: BRAND.green }}
            data-testid="aid-add-btn"
          >
            <Plus className="w-4 h-4" /> Add Record
          </button>
        </div>
      </div>

      {/* Active-filter chip bar */}
      {activeFilters.length > 0 && (
        <div
          className="rounded-xl border px-4 py-2.5 flex flex-wrap items-center gap-2 text-xs"
          style={{ background: "#FFFBEA", borderColor: "#f5e5a1" }}
          data-testid="aid-filter-chips"
        >
          {activeFilters.map(([col, set]) => (
            <div
              key={col}
              className="inline-flex items-center gap-1 rounded-full bg-white/70 border border-amber-200 px-2 py-1"
            >
              <span className="font-semibold uppercase tracking-wide text-[10px] text-amber-800">
                {col.replace(/_/g, " ")}
              </span>
              <span className="text-amber-700">
                {Array.from(set).map((v) => (v === "" ? "(Blanks)" : v)).join(", ")}
              </span>
              <button
                onClick={() => setColFilter(col)(null)}
                className="ml-1 text-amber-600 hover:text-amber-900"
                aria-label={`Clear ${col} filter`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <button
            onClick={clearAllFilters}
            className="ml-auto text-amber-800 hover:underline font-semibold"
            data-testid="aid-filters-clear-all"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Table */}
      <div
        className="rounded-2xl border shadow-sm overflow-hidden bg-white"
        style={{ borderColor: "#dfe7d8" }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead
              className="text-left text-[10px] uppercase tracking-wider"
              style={{
                background: `linear-gradient(90deg, ${BRAND.green} 0%, ${BRAND.greenDark} 100%)`,
                color: "white",
              }}
            >
              <tr>
                <th className="px-3 py-3 sticky left-0 z-10" style={{ background: BRAND.green }}>Student ID</th>
                <th className="px-3 py-3">Name</th>
                <th className="px-3 py-3">
                  <FilterableHeader
                    col="program"
                    label="Program"
                    values={rows.map((r) => r.program || "")}
                    selected={filters.program}
                    onChange={setColFilter("program")}
                  />
                </th>
                <th className="px-3 py-3">
                  <FilterableHeader
                    col="province"
                    label="Province"
                    values={rows.map((r) => r.province || "")}
                    selected={filters.province}
                    onChange={setColFilter("province")}
                  />
                </th>
                <th className="px-3 py-3">
                  <FilterableHeader
                    col="agent"
                    label="Agent"
                    values={rows.map((r) => r.agent || "")}
                    selected={filters.agent}
                    onChange={setColFilter("agent")}
                  />
                </th>
                <th className="px-3 py-3">Start</th>
                <th className="px-3 py-3">End</th>
                <th className="px-3 py-3">Email</th>
                <th className="px-3 py-3 text-center" title="Passport / PR">Ppt/PR</th>
                <th className="px-3 py-3 text-center" title="Photo ID">Photo</th>
                <th className="px-3 py-3 text-center" title="Transcript / 12 (ACSW)">Tr/12</th>
                <th className="px-3 py-3 text-center" title="Language Proof">Lang</th>
                <th className="px-3 py-3">
                  <FilterableHeader
                    col="action"
                    label="Action"
                    values={rows.map((r) => r.action || "")}
                    selected={filters.action}
                    onChange={setColFilter("action")}
                  />
                </th>
                <th className="px-3 py-3">Pending Docs</th>
                <th className="px-3 py-3">Appendix 3</th>
                <th className="px-3 py-3">
                  <FilterableHeader
                    col="coe_status"
                    label="COE"
                    values={rows.map((r) => r.coe_status || "")}
                    selected={filters.coe_status}
                    onChange={setColFilter("coe_status")}
                    valueLabel={(v) => (v === "done" ? "Done" : v === "not_done" ? "Not done" : "(Blanks)")}
                  />
                </th>
                <th className="px-3 py-3">
                  <FilterableHeader
                    col="sec_form"
                    label="SEC"
                    values={rows.map((r) => r.sec_form || "")}
                    selected={filters.sec_form}
                    onChange={setColFilter("sec_form")}
                    valueLabel={(v) => (v === "yes" ? "Yes" : v === "no" ? "No" : "(Blanks)")}
                  />
                </th>
                <th className="px-3 py-3">Comments</th>
                <th
                  className="px-3 py-3 text-right sticky right-0 z-10"
                  style={{ background: BRAND.green }}
                >
                  &nbsp;
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={19} className="text-center py-16 text-slate-400">
                    <ClipboardList className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    No student aid records yet
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr
                    key={r.id}
                    data-testid={`aid-row-${r.id}`}
                    className="border-t"
                    style={{
                      borderColor: "#eef1e9",
                      background: idx % 2 === 0 ? "white" : BRAND.hover,
                    }}
                  >
                    <td
                      className="px-3 py-2 font-mono text-slate-700 sticky left-0"
                      style={{ background: idx % 2 === 0 ? "white" : BRAND.hover }}
                    >
                      {r.student_id || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold text-slate-800 whitespace-nowrap">
                        {r.last_name}, {r.first_name}
                      </div>
                      {r.email && (
                        <div className="text-[10px] text-slate-400 truncate max-w-[220px]">
                          {r.email}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2"><ProgramPill code={r.program} /></td>
                    <td className="px-3 py-2"><ProvincePill p={r.province} /></td>
                    <td className="px-3 py-2 text-slate-700 truncate max-w-[180px]">{r.agent || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{r.start_date}</td>
                    <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{r.end_date}</td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[180px]">{r.email}</td>
                    <td className="px-3 py-2 text-center"><DocPill v={r.passport_pr_card} /></td>
                    <td className="px-3 py-2 text-center"><DocPill v={r.photo_id} /></td>
                    <td className="px-3 py-2 text-center"><DocPill v={r.transcript_12_acsw} /></td>
                    <td className="px-3 py-2 text-center"><DocPill v={r.language_proof} /></td>
                    <td className="px-3 py-2"><ActionPill v={r.action} /></td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[180px]">{r.pending_documents || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[140px]">{r.appendix_3 || <span className="text-slate-300">—</span>}</td>
                    <td className="px-3 py-2"><CoeBadge v={r.coe_status} /></td>
                    <td className="px-3 py-2"><SecBadge v={r.sec_form} /></td>
                    <td className="px-3 py-2 text-slate-600 truncate max-w-[220px]">{r.comments || <span className="text-slate-300">—</span>}</td>
                    <td
                      className="px-3 py-2 sticky right-0"
                      style={{ background: idx % 2 === 0 ? "white" : BRAND.hover }}
                    >
                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => setEditing({ ...r })}
                          className="inline-flex items-center gap-1 text-xs font-semibold hover:underline"
                          style={{ color: BRAND.green }}
                          title="Edit"
                          data-testid={`aid-edit-${r.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => removeOne(r.id)}
                          className="inline-flex items-center gap-1 text-rose-500 hover:underline text-xs font-semibold"
                          title="Delete"
                          data-testid={`aid-delete-${r.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <RecordModal
          record={editing}
          agentOptions={agentOptions}
          onClose={() => setEditing(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Add / Edit modal
// -----------------------------------------------------------------------------
function SectionHeader({ children }) {
  return (
    <div className="flex items-center gap-2 mt-6 mb-3 first:mt-0">
      <span
        className="w-2 h-2 rounded-full"
        style={{ background: BRAND.gold }}
      />
      <div
        className="text-[11px] uppercase tracking-widest font-semibold"
        style={{ color: BRAND.green }}
      >
        {children}
      </div>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

const inputCls =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-emerald-500";

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">
        {label} {required && <span className="text-rose-500">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function RecordModal({ record, agentOptions, onClose, onSave }) {
  // Legacy tolerance: coerce doc checklist to string form
  const initial = useMemo(() => {
    const r = { ...record };
    ["passport_pr_card", "photo_id", "transcript_12_acsw", "language_proof", "sec_form"].forEach(
      (k) => { r[k] = coerceYesNo(r[k]); },
    );
    if (!r.coe_status) r.coe_status = "not_done";
    return r;
  }, [record]);

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record.id;
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!form.student_id?.trim()) return toast.error("Student ID is required");
    if (!form.last_name?.trim() || !form.first_name?.trim())
      return toast.error("First name and Last name are required");
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="aid-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl my-6 bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
      >
        <div
          className="px-6 py-4 flex items-center justify-between sticky top-0 z-10"
          style={{ background: `linear-gradient(90deg, ${BRAND.green}, ${BRAND.greenDark})` }}
        >
          <div className="text-white">
            <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: BRAND.gold }}>
              Student Aid
            </div>
            <div className="font-display text-lg font-semibold">
              {isEdit ? "Edit student record" : "Add student record"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white"
            data-testid="aid-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1" data-testid="aid-modal-body">
          {/* 1. Basic Info */}
          <SectionHeader>Basic Info</SectionHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Student ID" required>
              <input
                value={form.student_id}
                onChange={(e) => set("student_id", e.target.value)}
                className={inputCls}
                data-testid="aid-field-student_id"
              />
            </Field>
            <Field label="Last Name" required>
              <input
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
                className={inputCls}
                data-testid="aid-field-last_name"
              />
            </Field>
            <Field label="First Name" required>
              <input
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                className={inputCls}
                data-testid="aid-field-first_name"
              />
            </Field>
            <Field label="Email" className="md:col-span-3">
              <input
                type="email"
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
                className={inputCls}
                data-testid="aid-field-email"
              />
            </Field>
          </div>

          {/* 2. Program & Duration */}
          <SectionHeader>Program & Duration</SectionHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Program">
              <select
                value={form.program}
                onChange={(e) => set("program", e.target.value)}
                className={inputCls}
                data-testid="aid-field-program"
              >
                <option value="">— Select —</option>
                {AID_PROGRAMS.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.label} ({p.code})
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Province">
              <select
                value={form.province}
                onChange={(e) => set("province", e.target.value)}
                className={inputCls}
                data-testid="aid-field-province"
              >
                <option value="">— Select —</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </Field>
            <Field label="Agent / Agency">
              <select
                value={form.agent}
                onChange={(e) => set("agent", e.target.value)}
                className={inputCls}
                data-testid="aid-field-agent"
              >
                <option value="">— Select —</option>
                {agentOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Start Date">
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => set("start_date", e.target.value)}
                className={inputCls}
                data-testid="aid-field-start_date"
              />
            </Field>
            <Field label="End Date">
              <input
                type="date"
                value={form.end_date}
                onChange={(e) => set("end_date", e.target.value)}
                className={inputCls}
                data-testid="aid-field-end_date"
              />
            </Field>
          </div>

          {/* 3. Document Checklist */}
          <SectionHeader>Document Checklist</SectionHeader>
          <div
            className="rounded-xl p-4 border"
            style={{ background: "#FBFAF3", borderColor: "#efeadb" }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["passport_pr_card", "Passport / PR Card"],
                ["photo_id", "Photo ID Proof"],
                ["transcript_12_acsw", "Transcript / 12 (ACSW)"],
                ["language_proof", "Language Proof"],
              ].map(([k, label]) => (
                <Field key={k} label={label}>
                  <select
                    value={form[k]}
                    onChange={(e) => set(k, e.target.value)}
                    className={inputCls}
                    data-testid={`aid-field-${k}`}
                  >
                    {AID_YES_NO.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </Field>
              ))}
            </div>
          </div>

          {/* 4. Status & Actions */}
          <SectionHeader>Status & Actions</SectionHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Action">
              <select
                value={form.action}
                onChange={(e) => set("action", e.target.value)}
                className={inputCls}
                data-testid="aid-field-action"
              >
                <option value="">— Select —</option>
                {AID_ACTIONS.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </Field>
            <Field label="COE Status">
              <select
                value={form.coe_status}
                onChange={(e) => set("coe_status", e.target.value)}
                className={inputCls}
                data-testid="aid-field-coe_status"
              >
                {AID_COE_STATUS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="SEC Form">
              <select
                value={form.sec_form}
                onChange={(e) => set("sec_form", e.target.value)}
                className={inputCls}
                data-testid="aid-field-sec_form"
              >
                {AID_YES_NO.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Appendix 3" className="md:col-span-3">
              <input
                value={form.appendix_3}
                onChange={(e) => set("appendix_3", e.target.value)}
                className={inputCls}
                placeholder="e.g. Sent 2026-02-14"
                data-testid="aid-field-appendix_3"
              />
            </Field>
          </div>

          {/* 5. Notes */}
          <SectionHeader>Notes</SectionHeader>
          <div className="grid grid-cols-1 gap-4">
            <Field label="Pending Documents">
              <textarea
                value={form.pending_documents}
                onChange={(e) => set("pending_documents", e.target.value)}
                rows={2}
                className={inputCls}
                data-testid="aid-field-pending_documents"
              />
            </Field>
            <Field label="Comments">
              <textarea
                value={form.comments}
                onChange={(e) => set("comments", e.target.value)}
                rows={3}
                className={inputCls}
                data-testid="aid-field-comments"
              />
            </Field>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="aid-modal-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="inline-flex items-center gap-1.5 text-white text-sm font-semibold px-5 py-2 rounded-md disabled:opacity-70"
            style={{ background: BRAND.green }}
            data-testid="aid-modal-save"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" /> Saving...
              </>
            ) : (
              "Save Record"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
